import { useSetToast } from "@app/components/Toast";
import { Chat, TitleChip } from "@app/shared/components/ai/Chat";
import { FlexCol } from "@design-system/Layout/Flex";
import { ResponseStatus, type ScoreValue } from "@generated/client/graphql";
import { type FC } from "react";
import { useParams } from "react-router-dom";
import {
    useEvsExpertChat,
    useScoreResponse,
    useTitleGenerator,
} from "../../data";
import { useAgentBreadcrumb } from "../../useAgentBreadcrumb";
import { useEvsExpertAgentTranslations } from "./i18n";

export const EvsExpertAgent: FC = () => {
    const { agent_id } = useParams();
    const { t, keys } = useEvsExpertAgentTranslations();

    const {
        messages,
        input,
        handleInputChange,
        handleSubmit,
        status,
        reload,
        error,
        append,
        stop,
    } = useEvsExpertChat();

    const { scoreResponse } = useScoreResponse();

    const { setToastSuccess, setToastError } = useSetToast();

    const title = useTitleGenerator(messages, status, 2);
    useAgentBreadcrumb(agent_id ?? "", title);

    const date = messages?.[0]?.createdAt || new Date();

    const exampleMessages = [
        t(keys.example_message_chip_assessment),
        t(keys.example_message_chip_monitoring),
        t(keys.example_message_chip_living_wage),
        t(keys.example_message_chip_policy),
    ];

    const isLoading = status === "submitted";

    const isDisabled = status !== "ready" || !input.length || !!error;

    const showStopButton = status === "streaming" || status === "submitted";

    const handleScoreResponse = (messageId: string) => {
        return (scoreValue: ScoreValue) => {
            scoreResponse(
                { traceId: messageId, scoreValue },
                {
                    onSettled(data) {
                        if (
                            data?.scoreResponse.status ===
                            ResponseStatus.Success
                        ) {
                            setToastSuccess(t(keys.score_response_success));
                            return;
                        }
                        setToastError(t(keys.score_response_error));
                    },
                },
            );
        };
    };

    const handleExampleMessageChipClick = (message: string) => {
        return () => {
            append({
                id: crypto.randomUUID(),
                role: "user",
                content: message,
            });
        };
    };

    return (
        <FlexCol
            w="full"
            alignItems="center"
            className="h-[calc(100%-8px)] bg-white relative"
        >
            <TitleChip
                title={t(keys.title_chip_title)}
                className="absolute top-0 left-0"
            />
            <Chat>
                {!messages.length ? (
                    <Chat.EmptyState
                        imageUrl="https://beavr-static.s3.eu-west-3.amazonaws.com/public/frameworks/evs.png"
                        title={t(keys.empty_state_title)}
                    />
                ) : (
                    <Chat.Content>
                        <Chat.Date date={date} />

                        {messages.map((message, idx) =>
                            message.role === "assistant" ? (
                                <Chat.AiMessage
                                    key={message.id}
                                    withCopy
                                    onRegenerate={
                                        idx === messages.length - 1
                                            ? reload
                                            : undefined
                                    }
                                    onFeedback={
                                        status === "ready" ||
                                        idx !== messages.length - 1
                                            ? handleScoreResponse(message.id)
                                            : undefined
                                    }
                                >
                                    {message.content}
                                </Chat.AiMessage>
                            ) : (
                                <Chat.UserMessage
                                    key={message.id}
                                    scrollToMessage={
                                        idx === messages.length - 1
                                    }
                                >
                                    {message.content}
                                </Chat.UserMessage>
                            ),
                        )}
                        {isLoading && <Chat.LoadingMessage />}
                        {!!error && (
                            <Chat.ErrorMessage>
                                {t(keys.error_message)}
                            </Chat.ErrorMessage>
                        )}
                    </Chat.Content>
                )}
                <Chat.ScrollToBottomButton />
                <Chat.Input
                    onSubmit={handleSubmit}
                    placeholder={t(keys.ask_me_anything)}
                    input={input}
                    disabled={isDisabled}
                    onStop={showStopButton ? stop : undefined}
                    onInputChange={handleInputChange}
                />
                {!messages.length && (
                    <Chat.ExampleMessageSection>
                        {exampleMessages.map((message) => (
                            <Chat.ExampleMessageChip
                                key={message}
                                onClick={handleExampleMessageChipClick(message)}
                            >
                                {message}
                            </Chat.ExampleMessageChip>
                        ))}
                    </Chat.ExampleMessageSection>
                )}
            </Chat>
        </FlexCol>
    );
};
